import React, { useEffect, useState, useRef } from 'react'; // Importa useRef
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import UrlConfig from '../../data/UrlConfig';

// Iconos para feedback visual
const CheckCircleIcon = () => <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-16 h-16 text-green-400 mx-auto mb-4"><path fillRule="evenodd" d="M2.25 12c0-5.385 4.365-9.75 9.75-9.75s9.75 4.365 9.75 9.75-4.365 9.75-9.75 9.75S2.25 17.385 2.25 12Zm13.36-1.814a.75.75 0 1 0-1.06-1.06l-3.894 3.893-1.48-1.48a.75.75 0 0 0-1.06 1.061l2.014 2.015a.75.75 0 0 0 1.06 0l4.42-4.42Z" clipRule="evenodd" /></svg>;
const ExclamationCircleIcon = () => <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-16 h-16 text-red-500 mx-auto mb-4"><path fillRule="evenodd" d="M12 2.25c-5.385 0-9.75 4.365-9.75 9.75s4.365 9.75 9.75 9.75 9.75-4.365 9.75-9.75S17.385 2.25 12 2.25Zm-1.72 6.97a.75.75 0 1 0-1.06 1.06L10.94 12l-1.72 1.72a.75.75 0 1 0 1.06 1.06L12 13.06l1.72 1.72a.75.75 0 1 0 1.06-1.06L13.06 12l1.72-1.72a.75.75 0 1 0-1.06-1.06L12 10.94l-1.72-1.72Z" clipRule="evenodd" /></svg>;

const VerifyEmailPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  
  const [status, setStatus] = useState('verifying'); // 'verifying', 'success', 'error'
  const [message, setMessage] = useState('Verificando tu cuenta...');
  
  // Usamos un useRef para asegurarnos de que la solicitud de verificación se envíe solo una vez.
  // Esto es especialmente útil en el modo estricto de React, que puede ejecutar useEffect dos veces en desarrollo.
  const hasFetchedRef = useRef(false); 

  useEffect(() => {
    const token = searchParams.get('token');

    // Si ya se ha intentado (o completado) la verificación, salimos del efecto para evitar duplicados.
    if (hasFetchedRef.current) {
      return;
    }

    if (!token) {
      setStatus('error');
      setMessage('No se encontró un token de verificación en la URL.');
      hasFetchedRef.current = true; // Marcamos como intentado incluso si hay error inicial.
      return;
    }

    const verifyToken = async () => {
      hasFetchedRef.current = true; // Marcamos que la verificación ha sido intentada.
      try {
        // La concatenación de URL original con '&' se mantiene asumiendo que UrlConfig.auth_verifyEmail
        // ya incluye un '?' y que el token es un parámetro adicional.
        const response = await fetch(`${UrlConfig.auth_verifyEmail}&token=${token}`, {
          method: 'GET',
          credentials: 'include',
        });
        
        const data = await response.json();

        if (!response.ok) {
          throw new Error(data.message || 'Ocurrió un error desconocido.');
        }

        setStatus('success');
        setMessage(data.message);

        setTimeout(() => {
          navigate('/login?verified=true', { replace: true });
        }, 3000);

      } catch (error) {
        setStatus('error');
        setMessage(error.message);
      }
    };

    verifyToken();
  }, [searchParams, navigate]); // hasFetchedRef no se incluye en las dependencias porque es una ref.

  return (
    <div className="min-h-screen bg-black flex flex-col justify-center items-center p-4 text-center">
      <div className="w-full max-w-md bg-gray-900/50 border border-yellow-500/20 rounded-xl p-8 shadow-2xl shadow-yellow-500/10">
        {status === 'verifying' && (
          <h1 className="text-2xl font-bold text-yellow-400 animate-pulse">{message}</h1>
        )}

        {status === 'success' && (
          <div>
            <CheckCircleIcon />
            <h1 className="text-2xl font-bold text-green-400">¡Éxito!</h1>
            <p className="text-gray-300 mt-2">{message}</p>
            <p className="text-gray-400 mt-4">Serás redirigido en unos segundos...</p>
          </div>
        )}

        {status === 'error' && (
          <div>
            <ExclamationCircleIcon />
            <h1 className="text-2xl font-bold text-red-500">Error de Verificación</h1>
            <p className="text-gray-300 mt-2">{message}</p>
            <Link 
              to="/signup" 
              className="mt-6 inline-block text-yellow-400 hover:text-yellow-300"
            >
              Intentar registrarse de nuevo
            </Link>
          </div>
        )}
      </div>
    </div>
  );
};

export default VerifyEmailPage;